import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import PilgrimStatsCard from './components/PilgrimStatsCard';
import FilterBar from './components/FilterBar';
import PilgrimTableRow from './components/PilgrimTableRow';
import PilgrimMobileCard from './components/PilgrimMobileCard';
import BulkActionsBar from './components/BulkActionsBar';
import AuditTrailPanel from './components/AuditTrailPanel';
import { Checkbox } from '../../components/ui/Checkbox';
import { getPilgrims, deletePilgrim } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { exportPilgrimsToPDF, exportPilgrimsToCSV } from '../../utils/exportUtils';

const PilgrimManagement = () => {
  const navigate = useNavigate();
  const { currentLanguage, t } = useLanguage();
  const { profile, loading: authLoading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedPilgrims, setSelectedPilgrims] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('all');
  const [pilgrims, setPilgrims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    loadPilgrims();
  }, []);

  const loadPilgrims = async () => {
    try {
      setLoading(true);
      const data = await getPilgrims();
      setPilgrims(data || []);
    } catch (err) {
      console.error('Error loading pilgrims:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePilgrim = async (id) => {
    if (window.confirm(t('pilgrims.confirmDelete'))) {
      try {
        await deletePilgrim(id);
        await loadPilgrims();
      } catch (err) {
        console.error('Error deleting pilgrim:', err);
        alert(t('pilgrims.deleteError'));
      }
    }
  };

  const mockUser = profile || {
    name: 'Ahmed Benali',
    role: 'agency_admin',
    agency: 'Agence Al-Baraka Voyages',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
  };

  const mockNotifications = [];


  const filteredPilgrims = pilgrims?.filter((pilgrim) => {
    const matchesSearch =
    pilgrim?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    pilgrim?.passportNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    pilgrim?.phone?.includes(searchQuery) ||
    pilgrim?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());

    const matchesStatus = statusFilter === 'all' || pilgrim?.visaStatus === statusFilter;
    const matchesGroup = groupFilter === 'all' || pilgrim?.group === groupFilter;

    return matchesSearch && matchesStatus && matchesGroup;
  });

  const sortedPilgrims = [...filteredPilgrims]?.sort((a, b) => {
    if (sortConfig?.key) {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const totalPages = Math.ceil(sortedPilgrims?.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedPilgrims = sortedPilgrims?.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPilgrims(paginatedPilgrims?.map((p) => p?.id));
    } else {
      setSelectedPilgrims([]);
    }
  };

  const handleSelectPilgrim = (id, checked) => {
    if (checked) {
      setSelectedPilgrims((prev) => [...prev, id]);
    } else {
      setSelectedPilgrims((prev) => prev?.filter((pid) => pid !== id));
    }
  };

  const handleFilterChange = (key, value) => {
    if (key === 'status') {
      setStatusFilter(value);
    } else if (key === 'group') {
      setGroupFilter(value);
    }
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setGroupFilter('all');
    setSearchQuery('');
    setCurrentPage(1);
  };

  const handleExportPDF = () => {
    const exportData = selectedPilgrims?.length > 0 
      ? pilgrims?.filter(p => selectedPilgrims?.includes(p?.id))
      : filteredPilgrims;
    exportPilgrimsToPDF(exportData, currentLanguage);
  };

  const handleExportCSV = () => {
    const exportData = selectedPilgrims?.length > 0 
      ? pilgrims?.filter(p => selectedPilgrims?.includes(p?.id))
      : filteredPilgrims;
    exportPilgrimsToCSV(exportData);
  };

  const stats = {
    total: pilgrims?.length || 0,
    active: pilgrims?.filter(p => p?.visaStatus === 'approved')?.length || 0,
    pending: pilgrims?.filter(p => p?.visaStatus === 'pending')?.length || 0,
    incomplete: pilgrims?.filter(p => p?.documentStatus === 'incomplete' || p?.documentStatus === 'missing')?.length || 0
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      <div className={`transition-smooth ${isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`}>
        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-warm">
          <div className="flex items-center justify-between pl-16 pr-4 md:px-6 lg:px-8 py-4">
            <div className="flex-1" />
            <div className="flex items-center gap-3">
              <NotificationCenter
                notifications={mockNotifications}
                currentLanguage={currentLanguage}
                onNotificationClick={(id) => console.log('Notification clicked:', id)}
                onMarkAsRead={(id) => console.log('Mark as read:', id)} />

              <UserProfileDropdown
                user={mockUser}
                currentLanguage={currentLanguage}
                onLanguageChange={(newLanguage) => console.log('Language change:', newLanguage)}
                onLogout={() => navigate('/login')} />

            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <BreadcrumbNavigation currentLanguage={currentLanguage} />

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                {t('pilgrims.title')}
              </h1>
              <p className="text-muted-foreground">
                {t('pilgrims.manageSubtitle')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="outline"
                iconName="Upload"
                iconPosition="left">
                {t('pilgrims.import')}
              </Button>
              <Button
                variant="outline"
                iconName="FileText"
                iconPosition="left"
                onClick={handleExportPDF}>
                {t('pilgrims.exportPDF')}
              </Button>
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                onClick={handleExportCSV}>
                {t('pilgrims.exportCSV')}
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                iconPosition="left"
                onClick={() => navigate('/pilgrim-profile')}>
                {t('pilgrims.addPilgrim')}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
            <PilgrimStatsCard
              icon="Users"
              label={t('pilgrims.totalPilgrims')}
              value={stats?.total}
              trend="up"
              trendValue="+12%"
              variant="primary" />

            <PilgrimStatsCard
              icon="CheckCircle2"
              label={t('pilgrims.visaApprovalRate')}
              value={`${Math.round(stats?.active / stats?.total * 100)}%`}
              trend="up"
              trendValue="+5%"
              variant="success" />

            <PilgrimStatsCard
              icon="FileCheck"
              label={t('pilgrims.completeDocuments')}
              value={`${Math.round((stats?.total - stats?.incomplete) / stats?.total * 100)}%`}
              trend="up"
              trendValue="+8%"
              variant="default" />

            <PilgrimStatsCard
              icon="DollarSign"
              label={t('pilgrims.paymentsReceived')}
              value={`${Math.round((stats?.active + stats?.pending) / stats?.total * 100)}%`}
              trend="down"
              trendValue="-3%"
              variant="warning" />

          </div>

          <FilterBar
            filters={{
              status: statusFilter,
              group: groupFilter
            }}
            onFilterChange={handleFilterChange}
            onSearch={setSearchQuery}
            onClearFilters={handleClearFilters}
            searchQuery={searchQuery}
            currentLanguage={currentLanguage} />


          <div className="hidden lg:block bg-card border border-border rounded-lg shadow-warm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted border-b border-border">
                  <tr>
                    <th className="p-4 text-left">
                      <Checkbox
                        checked={selectedPilgrims?.length === paginatedPilgrims?.length && paginatedPilgrims?.length > 0}
                        onChange={(e) => handleSelectAll(e?.target?.checked)} />

                    </th>
                    <th className="p-4 text-left">
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-2 font-medium text-foreground hover:text-primary transition-smooth">
                        {t('pilgrims.name')}
                        <Icon name="ArrowUpDown" size={16} />
                      </button>
                    </th>
                    <th className="p-4 text-left">
                      <span className="font-medium text-foreground">{t('pilgrims.contact')}</span>
                    </th>
                    <th className="p-4 text-left">
                      <button
                        onClick={() => handleSort('group')}
                        className="flex items-center gap-2 font-medium text-foreground hover:text-primary transition-smooth">
                        {t('pilgrims.group')}
                        <Icon name="ArrowUpDown" size={16} />
                      </button>
                    </th>
                    <th className="p-4 text-left">
                      <span className="font-medium text-foreground">{t('pilgrims.visa')}</span>
                    </th>
                    <th className="p-4 text-left">
                      <span className="font-medium text-foreground">{t('pilgrims.documents')}</span>
                    </th>
                    <th className="p-4 text-left">
                      <span className="font-medium text-foreground">{t('pilgrims.payment')}</span>
                    </th>
                    <th className="p-4 text-left">
                      <button
                        onClick={() => handleSort('caseProgress')}
                        className="flex items-center gap-2 font-medium text-foreground hover:text-primary transition-smooth">
                        {t('pilgrims.progress')}
                        <Icon name="ArrowUpDown" size={16} />
                      </button>
                    </th>
                    <th className="p-4 text-right">
                      <span className="font-medium text-foreground">{t('pilgrims.actions')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPilgrims?.map((pilgrim) =>
                  <PilgrimTableRow
                    key={pilgrim?.id}
                    pilgrim={pilgrim}
                    isSelected={selectedPilgrims?.includes(pilgrim?.id)}
                    onSelect={handleSelectPilgrim}
                    currentLanguage={currentLanguage} />

                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 border-t border-border">
              <p className="caption text-muted-foreground">
                {t('pilgrims.showing')} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedPilgrims?.length)} {t('pilgrims.of')} {sortedPilgrims?.length} {t('pilgrims.pilgrimsText')}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronLeft"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)}>
                  {t('pilgrims.previous')}
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)?.map((page) =>
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 rounded-md transition-smooth ${
                    currentPage === page ?
                    'bg-primary text-primary-foreground' :
                    'bg-muted text-foreground hover:bg-muted/80'}`
                    }>

                      {page}
                    </button>
                  )}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronRight"
                  iconPosition="right"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)}>
                  {t('pilgrims.next')}
                </Button>
              </div>
            </div>
          </div>

          <div className="lg:hidden space-y-4">
            {paginatedPilgrims?.map((pilgrim) =>
            <PilgrimMobileCard
              key={pilgrim?.id}
              pilgrim={pilgrim}
              isSelected={selectedPilgrims?.includes(pilgrim?.id)}
              onSelect={handleSelectPilgrim}
              currentLanguage={currentLanguage} />

            )}

            <div className="flex flex-col items-center gap-4 mt-6">
              <p className="caption text-muted-foreground">
                {t('pilgrims.showing')} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedPilgrims?.length)} {t('pilgrims.of')} {sortedPilgrims?.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronLeft"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((prev) => prev - 1)} />

                <span className="caption text-foreground">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ChevronRight"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((prev) => prev + 1)} />

              </div>
            </div>
          </div>

          <AuditTrailPanel />
        </main>

        <BulkActionsBar
          selectedCount={selectedPilgrims?.length}
          onAssignGroup={() => console.log('Assign group')}
          onRequestDocuments={() => console.log('Request documents')}
          onSendMessage={() => console.log('Send message')}
          onExport={handleExportPDF}
          onClearSelection={() => setSelectedPilgrims([])}
          currentLanguage={currentLanguage}
        />
      </div>
    </div>
  );

};

export default PilgrimManagement;